import {
  Box,
  Button,
  Card,
  DialogContent,
  DialogTitle,
  Checkbox,
  LinearProgress,
  linearProgressClasses,
  Tab,
  Tabs,
  Typography,
  Paper,
} from "@mui/material";

import { tabsClasses } from "@mui/material/Tabs";
import { styled } from "@mui/material/styles";
import colors from "../../../theme/Colours";

export const LargeBoldText = styled(Typography)(({ theme }) => ({
  fontFamily: "Open Sans, sans-serif",
  fontWeight: 700,
  fontSize: "40px",
  [theme.breakpoints.down("md")]: {
    fontSize: "36px",
  },
}));

export const SemiBoldText = styled(Typography)(({ theme }) => ({
  fontFamily: "Open Sans, sans-serif",
  fontWeight: 600,
  fontSize: "16px",
  [theme.breakpoints.down("md")]: {
    fontSize: "14px",
  },
}));

export const RegularText = styled(Typography)(({ theme }) => ({
  fontFamily: "Open Sans, sans-serif",
  fontWeight: 400,
  fontSize: "14px",
  [theme.breakpoints.down("md")]: {
    fontSize: "10px",
  },
}));

export const HelperText = styled(Typography)(({ theme }) => ({
  fontFamily: "Open Sans, sans-serif",
  fontWeight: 500,
  fontSize: "14px",
  color: colors.salesAnalyticsCardHelperText,
  [theme.breakpoints.down("md")]: {
    fontSize: "10px",
  },
}));

export const SalesAnalyticsCardLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "Open Sans, sans-serif",
  fontWeight: 600,
  fontSize: "16px",
  color: colors.salesAnalyticsCardLabelText,
  [theme.breakpoints.down("md")]: {
    fontSize: "14px",
  },
}));

export const StyledFilterButtonContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  height: "40px",
  gap: theme.spacing(2),
  paddingRight: theme.spacing(3),
}));

export const StyledGlobalFiltersContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  width: "100%",
  minWidth: 0,

  [theme.breakpoints.down("lg")]: {
    flexWrap: "wrap",
    alignItems: "flex-start",
  },
}));

export const StyledGlobalFiltersFields = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "flex-start",
  gap: theme.spacing(1.5),
  flexWrap: "nowrap",
  minWidth: 0,

  [theme.breakpoints.down("lg")]: {
    flexWrap: "wrap",
  },
}));

export const StyledDashboardFilterBar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  // padding: `0 ${theme.spacing(3)}`,
  width: "100%",
  [theme.breakpoints.down("lg")]: {
    alignItems: "flex-start",
    flexDirection: "column",
  },
}));

export const StyledDashboardSelectorSlot = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  flex: "0 0 auto",
  marginLeft: "auto",
  flexDirection: "row",

  [theme.breakpoints.down("lg")]: {
    width: "100%",
    maxWidth: 260,
    marginLeft: 0,
    justifyContent: "space-between",
  },
}));

export const StyledKpiCardsContainer = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  display: "flex",
  gap: theme.spacing(3),
  flexWrap: "nowrap",
  "& > *": {
    flex: "1",
    minWidth: 0,
    [theme.breakpoints.down("lg")]: {
      flex: "1 1 calc(50% - 9px)",
    },
    [theme.breakpoints.down("sm")]: {
      flex: "1 1 100%",
    },
  },
}));

export const StyledKpiCard = styled(Card)(({ theme }) => ({
  height: "auto",
  borderRadius: "8px",
  backgroundColor: "#FFFFFF",
  boxShadow: `2px 2px 4px 0px ${colors.sortContainerBoxShadow}`,
  padding: theme.spacing(1),
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
}));

export const StyledRootContainer = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(9),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingBottom: theme.spacing(2),
}));

export const StyledTabsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  paddingTop: theme.spacing(0.5),
  borderBottom: `1px solid ${theme.palette.divider}`,

  [theme.breakpoints.down("lg")]: {
    flexDirection: "column",
    alignItems: "stretch",
  },
}));

export const StyledTabPanelContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 3),
}));

export const StyledTab = styled(Tab)(() => ({
  fontFamily: "Open Sans, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  textTransform: "unset",

  "&.Mui-selected": {
    fontWeight: 700,
    color: colors.primaryButtonOrange,
  },
}));

export const StyledTabsIndicator = styled(Tabs)(() => ({
  [`& .${tabsClasses.indicator}`]: {
    backgroundColor: colors.primaryButtonOrange,
    height: 4,
    borderRadius: 2,
  },
}));

export const StyledPrimaryButton = styled(Button)(({ theme }) => ({
  width: "100px",
  height: "42px",
  borderRadius: "4px",
  backgroundColor: colors.primaryButtonOrange,
  color: colors.background2,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  border: "none",
  "&:hover": {
    backgroundColor: colors.primaryButtonOrangeHover,
  },
  "&:disabled": {
    backgroundColor: colors.primaryButtonOrangeDisabled,
    borderColor: colors.primaryButtonOrangeDisabled,
    color: colors.background2,
    cursor: "not-allowed",
  },
}));

export const StyledSecondaryButton = styled(Button)(({ theme }) => ({
  width: "100px",
  height: "40px",
  borderRadius: "4px",
  backgroundColor: colors.background2,
  color: colors.primaryButtonOrange,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  border: `1px solid ${colors.primaryButtonOrange}`,
  "&:hover": {
    backgroundColor: colors.primaryButtonOrange,
    color: colors.background2,
  },
  "&:disabled": {
    color: colors.primaryButtonOrangeDisabled,
    borderColor: colors.primaryButtonOrangeDisabled,
    backgroundColor: colors.background2,
  },
}));

export const StyledResetButton = styled(Button)(({ theme }) => ({
  width: "100px",
  height: "42px",
  borderRadius: "4px",
  color: colors.primaryButtonOrange,
  borderColor: colors.primaryButtonOrange,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  "&:hover": {
    color: colors.background2,
    borderColor: colors.primaryButtonOrangeHover,
    backgroundColor: colors.primaryButtonOrangeHover,
  },
  "&:disabled": {
    color: colors.primaryButtonOrangeDisabled,
    borderColor: colors.primaryButtonOrangeDisabled,
    backgroundColor: colors.background2,
  },
}));

export const StyledComparisonFilterContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-end",
  gap: theme.spacing(2),
  flexWrap: "wrap",
  justifyContent: "space-between",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(3),
  },
}));

export const StyledSalesAnalyticsCardsBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

export const StyledSalesAnalyticsCard = styled(Card)(({ theme }) => ({
  height: "330px",
  borderRadius: "8px",
  backgroundColor: colors.background2,
  boxShadow: `2px 2px 4px 0px ${colors.sortContainerBoxShadow}`,
  padding: theme.spacing(4),
  "&:focus, & *:focus": {
    outline: "none !important",
    boxShadow: "none !important",
  },
}));

export const StyledWodByProductCard = styled(StyledSalesAnalyticsCard)`
  & .recharts-text tspan {
    font-size: 10px !important;
  }

  /* Hide Recharts active-bar overlay */
  & .wod-product-chart .recharts-active-bar {
    display: none !important;
  }
`;

// WOD By Product Comparison Chart Containers
export const WodComparisonContainer = styled("div")(() => ({
  height: "295px",
}));

export const WodComparisonChartBox = styled(Box)<{ shouldScroll?: boolean }>(
  ({ shouldScroll }) => ({
    padding: "16px 0",
    overflowY: shouldScroll ? "auto" : "visible",
    maxHeight: shouldScroll ? "250px" : "none",
  })
);

export const WodProductRowBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(3),
}));

export const WodPeriodBarContainer = styled(Box)(() => ({
  width: "35%",
  display: "flex",
  alignItems: "center",
  gap: 6,
  "&.left": {
    justifyContent: "flex-end",
  },
  "&.right": {
    justifyContent: "flex-start",
  },
}));

export const WodProductNameContainer = styled(Box)(() => ({
  width: "25%",
  display: "flex",
  justifyContent: "center",
}));

export const WodBarBox = styled(Box)(({ theme }) => ({
  minWidth: 40,
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  position: "relative",
  height: 35,
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  "&.left": {
    justifyContent: "flex-start",
  },
  "&.right": {
    justifyContent: "flex-end",
  },
  "&:hover .WodBarText": {
    opacity: 1,
  },
}));

export const WodBarText = styled(Typography)(() => ({
  fontWeight: 500,
  color: colors.searchInputText,
  fontSize: 12,
  letterSpacing: 0.5,
  textAlign: "center",
  whiteSpace: "nowrap",
}));

export const WodProductNameText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: 13,
  textAlign: "center",
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  wordWrap: "break-word",
  lineHeight: 1.2,
}));

export const WodZeroBarBox = styled(Box)<{ productColor: string }>(
  ({ productColor }) => ({
    width: "3px",
    minWidth: "3px",
    backgroundColor: productColor,
    paddingLeft: 0,
    paddingRight: 0,
    position: "relative",
    height: 35,
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    "&::before": {
      content: '""',
      position: "absolute",
      top: "-5px",
      left: "-5px",
      right: "-5px",
      bottom: "-5px",
    },
    "&:hover .zero-text": {
      opacity: "1 !important",
      visibility: "visible !important",
    },
    "&.left": {
      justifyContent: "flex-start",
    },
    "&.right": {
      justifyContent: "flex-end",
    },
  })
);

export const WodZeroText = styled("span")<{
  productColor: string;
  position: "left" | "right";
}>(({ productColor, position }) => ({
  color: productColor,
  fontWeight: 600,
  fontSize: "12px",
  opacity: 0,
  visibility: "hidden",
  transition: "opacity 0.2s ease, visibility 0.2s ease",
  position: "absolute",
  ...(position === "left"
    ? {
        right: "calc(100% + 8px)",
      }
    : {
        left: "calc(100% + 8px)",
      }),
  top: "50%",
  transform: "translateY(-50%)",
  whiteSpace: "nowrap",
  pointerEvents: "none",
}));

export const WodTooltipWrapper = styled("div")<{ left: number; top: number }>(
  ({ left, top }) => ({
    position: "absolute",
    left,
    top: top + 20,
    width: 120,
    zIndex: 999,
  })
);

export const WodTooltipArrowBorder = styled("div")({
  position: "absolute",
  top: "-7px",
  left: "50%",
  transform: "translateX(-50%)",
  width: 0,
  height: 0,
  borderLeft: "7px solid transparent",
  borderRight: "7px solid transparent",
  borderBottom: `7px solid ${colors.wodTooltipArrowBorder}`,
});

export const WodTooltipArrow = styled("div")({
  position: "absolute",
  top: "-6px",
  left: "50%",
  transform: "translateX(-50%)",
  width: 0,
  height: 0,
  borderLeft: "6px solid transparent",
  borderRight: "6px solid transparent",
  borderBottom: `6px solid ${colors.background2}`,
  filter: "drop-shadow(0px -2px 1px rgba(0,0,0,0.1))",
});

export const wodAxisStyles = {
  fontSize: "10px",
};

export const StyledTooltipContainer = styled("div")(() => ({
  position: "relative",
  backgroundColor: colors.background2,
  border: `1px solid ${colors.greyBorder}`,
  borderRadius: 6,
  padding: "8px 10px",
  boxShadow: `0px 2px 4px ${colors.sortContainerBoxShadow}`,
}));

export const StyledTooltipProductText = styled("p")(() => ({
  margin: 0,
  fontSize: "12px",
  color: colors.salesAnalyticsCardLabelText,
  fontWeight: 500,
}));

export const StyledTooltipQuantityText = styled("p")(() => ({
  margin: 0,
  fontSize: "12px",
  color: colors.salesAnalyticsCardLabelText,
}));

export const StyledTooltipArrow = styled("div")(() => ({
  position: "absolute",
  left: "-6px",
  top: "50%",
  transform: "translateY(-50%)",
  width: 0,
  height: 0,
  borderTop: "6px solid transparent",
  borderBottom: "6px solid transparent",
  borderRight: `6px solid ${colors.background2}`,
}));

export const StyledTooltipArrowBorder = styled("div")(() => ({
  position: "absolute",
  left: "-7px",
  top: "50%",
  transform: "translateY(-50%)",
  width: 0,
  height: 0,
  borderTop: "7px solid transparent",
  borderBottom: "7px solid transparent",
  borderRight: `7px solid ${colors.greyBorder}`,
}));
export const StyledTooltipArrowRight = styled("div")({
  position: "absolute",
  top: "50%",
  right: "-6px",
  width: 0,
  height: 0,
  borderTop: "6px solid transparent",
  borderBottom: "6px solid transparent",
  borderLeft: `6px solid ${colors.background2}`,
  transform: "translateY(-50%)",
});

export const StyledTooltipArrowRightBorder = styled("div")({
  position: "absolute",
  top: "50%",
  right: "-7px",
  width: 0,
  height: 0,
  borderTop: "7px solid transparent",
  borderBottom: "7px solid transparent",
  borderLeft: `7px solid ${colors.greyBorder}`,
  transform: "translateY(-50%)",
});
export const SalesAnalyticsContentBox = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

export const SalesAnalyticsOverflowBox = styled(Box)(({ theme }) => ({
  overflowY: "auto",
  height: "295px",
  paddingRight: theme.spacing(1),
  outline: "none !important",
  "&:focus": {
    outline: "none !important",
  },
  "&:active": {
    outline: "none !important",
  },
}));

// WOD By Product Single Chart Container
export const WodSingleChartOverflowBox = styled(SalesAnalyticsOverflowBox)`
  overflow-x: auto;
  overflow-y: hidden;
`;

export const TopClientsByVolumeRootBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "hasComparison",
})<{ hasComparison?: boolean }>(({ theme, hasComparison }) => ({
  height: hasComparison ? "65px" : "45px",
  borderRadius: "3px",
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

export const TopClientsByVolumeInnerBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

export const ProductWodCurrentFlexBox = styled(Box)(() => ({
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
}));

export const ProductWodCurrentItemBox = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginBottom: theme.spacing(2),
  gap: theme.spacing(2),
  "& .wodChart:focus, & .wodChart *:focus": {
    outline: "none !important",
  },
  "& .wodChart .recharts-surface:focus": {
    outline: "none !important",
  },
}));

export const ProductWodCurrentProgressBox = styled(Box)(() => ({
  flexGrow: 1,
}));

export const StyledLinearProgress = styled(LinearProgress, {
  shouldForwardProp: (prop) => prop !== "percent",
})<{ percent: number }>(({ percent }) => ({
  height: 12,
  borderRadius: 6,
  backgroundColor: colors.greyBorder,
  [`& .${linearProgressClasses.bar}`]: {
    backgroundColor:
      percent <= 25
        ? colors.salesAnalyticsProductWodLow
        : percent <= 50
          ? colors.salesAnalyticsProductWodMedium
          : colors.salesAnalyticsProductWodHigh,
    borderRadius: 6,
  },
}));

export const StyledDialogTitle = styled(DialogTitle)({
  fontSize: "1.25rem",
  fontFamily: "Open Sans, sans-serif",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  paddingBottom: 0,
});

export const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(5),
  paddingRight: theme.spacing(5),
}));

export const StyledDialogButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  width: "25%",
  borderRadius: "8px",
  padding: theme.spacing(1, 4),
  fontWeight: 600,
  backgroundColor: colors.primaryButtonOrange,
  color: colors.background2,
  "&:hover": {
    backgroundColor: colors.primaryButtonOrangeHover,
  },
}));

export const StyledDialogHeading = styled(Typography)({
  fontFamily: "Open Sans, sans-serif",
  fontSize: "20px",
  fontWeight: 600,
  fontStyle: "normal",
  marginBottom: "24px",
  color: colors.buttonBGColor,
});

export const SectionTitle = styled(Typography)({
  fontFamily: "Open Sans, sans-serif",
  fontWeight: 400,
  paddingBottom: "8px",
});

export const IconWrapper = styled(Box)({
  cursor: "pointer",
  width: 20,
  height: 20,
});

// Dropdown trigger button
export const StyledSelectButton = styled(Button)(({ theme }) => ({
  width: "250px",
  borderRadius: "6px",
  textTransform: "none",
  backgroundColor: colors.background2,
  borderColor: "#777777",
  color: colors.searchInputText,
  fontSize: "15px",
  fontWeight: 600,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  "&:hover": {
    backgroundColor: colors.lightBackground,
  },
}));

// Dropdown menu container
export const StyledMenuPaper = styled(Paper)(() => ({
  marginTop: 8,
  borderRadius: "8px",
  boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
  width: "250px",
}));

// Period date range container
export const StyledPeriodContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  flexShrink: 0,
}));

// Period label typography
export const StyledPeriodLabel = styled(Typography)(() => ({
  fontFamily: "Open Sans, sans-serif",
  fontWeight: 600,
  fontSize: "12px",
  color: colors.searchInputText,
  paddingTop: "8px",
  paddingBottom: "8px",
}));

// Validation error container
export const StyledValidationErrorContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: "#ffebee",
  borderRadius: "4px",
  border: "1px solid colors.redText",
}));

// Validation error text
export const StyledValidationErrorText = styled(Typography)(() => ({
  color: colors.redText,
}));

// Dropdown components

export const StyledDropdownContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "inline-block",
  // paddingTop: 37,
  [theme.breakpoints.down("md")]: {
    paddingTop: 16,
  },
}));

export const StyledDropdownButton = styled(Button, {
  shouldForwardProp: (prop) =>
    prop !== "pageType" && prop !== "isClientAnalytics",
})<{
  pageType?: string;
  isClientAnalytics?: boolean;
}>(({ pageType }) => ({
  width: pageType === "sales-analytics" ? "250px" : "200px",
  borderRadius: "6px",
  textTransform: "none",
  backgroundColor: colors.background2,
  borderColor: colors.greyTextColor,
  color: colors.searchInputText,
  fontSize: "15px",
  fontWeight: 600,
  padding: "10px 16px",
  border: `1px solid ${colors.comparisionFiltersBorder}`,
  justifyContent: "space-between",
  alignItems: "center",
  height: "42px",
  "&:hover": {
    backgroundColor: colors.lightBackground,
  },
  "&.Mui-disabled": {
    backgroundColor: colors.lightGrey,
    color: colors.disabledGrey,
    borderColor: colors.comparisionFiltersBorder,
    cursor: "not-allowed",
  },
}));

export const StyledDropdownLabel = styled(Typography)(() => ({
  flex: 1,
  minWidth: 0,
  marginRight: 8,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  textAlign: "left",
  display: "block",
}));

export const StyledDropdownArrowContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isOpen",
})<{ isOpen: boolean }>(({ isOpen }) => ({
  transform: isOpen ? "rotate(0deg)" : "rotate(180deg)",
  transition: "transform 0.3s ease",
}));

export const StyledDropdownMenu = styled(Box)(() => ({
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  zIndex: 1000,
  backgroundColor: "white",
  border: "1px solid #777777",
  borderRadius: "8px",
  boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
  marginTop: "4px",
  overflow: "hidden",
}));

export const StyledDropdownScrollArea = styled(Box)(() => ({
  maxHeight: "300px",
  overflowY: "auto",
  padding: "4px 0",
}));

export const StyledDropdownItem = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  padding: "4px 12px",
  "&:hover": {
    backgroundColor: colors.buttonBG2,
  },
}));

export const StyledDropdownCheckbox = styled(Checkbox)(() => ({
  marginRight: "8px",
  marginLeft: "8px",
  padding: "4px",
  color: colors.black,
  "& .MuiSvgIcon-root": {
    fontSize: "18px",
  },
  "&.Mui-checked": {
    color: colors.progressColor,
  },
  "&.MuiCheckbox-indeterminate": {
    color: colors.progressColor,
  },
}));

export const LeftContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(4),
  [theme.breakpoints.down("md")]: {
    flexWrap: "wrap",
  },
}));
export const RightContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

export const StyledNoDataCard = styled("div")({
  border: `1px solid ${colors.noDataFoundCard}`,
  borderRadius: "8px",
  padding: "16px",
});

export const StyledNoDataLabel = styled(Typography)({
  marginBottom: "16px",
  fontWeight: 600,
});

export const StyledNoDataContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "300px",
});

export const StyledNoDataText = styled(Typography)({
  color: colors.noDataFoundText,
  textAlign: "center",
  fontSize: "14px",
});

export const OrderIntervalDropdownWrapper = styled(Box)(() => ({
  position: "relative",
  zIndex: 5,
}));

export const orderIntervalDropdownStyles = {
  button: {
    borderRadius: "4px",
    padding: "1px 3px",
    border: `1px solid ${colors.orderIntervalActiveBorder}`,
    background: colors.background2,
    fontWeight: 600 as const,
    justifyContent: "center" as const,
    gap: "2px",
  },
  label: { fontSize: 11 },
  option: {
    padding: "0px 2px",
    margin: "0px 1px",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 6 as const,
  },
  menu: {
    borderRadius: 8,
    width: 140,
    zIndex: 9999 as const,
    position: "absolute" as const,
    right: 0,
    left: "auto",
  },
  menuClass: "custom-dropdown-menu",
};

export const headerRightStyles = {
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 8,
  marginRight: "16px",
  gap: 12,
};
